// src/lib/pwa.js
// Small, dependency-free PWA helper: registers the service worker, holds
// onto the browser's "install this app" prompt so a custom button can
// trigger it (the browser's own install UI is easy to miss/dismiss and
// gives no control over when it's offered), and surfaces when an update
// is ready to install so the app can ask before reloading rather than
// doing it silently.
//
// Listener lists, not single overwritable callbacks — more than one part
// of the app wants to know about install/update availability (the main
// app's generic banner, and the kiosk display's own differently-styled
// one, since a generic fixed-position banner would collide with the
// kiosk's own full-screen layout). initPwa() is safe to call from more
// than one component: the actual registration work only happens once,
// every call after the first just adds another listener.

let deferredInstallPrompt = null;
const installListeners = [];
const updateListeners = [];
let waitingWorker = null;
let registered = false;

export function initPwa({ onInstallAvailable, onUpdateAvailable } = {}) {
  if (onInstallAvailable) installListeners.push(onInstallAvailable);
  if (onUpdateAvailable) updateListeners.push(onUpdateAvailable);

  // If install/update conditions were already detected before this
  // particular caller subscribed (e.g. the kiosk page mounts after the
  // main app already found an update), tell the new listener right away
  // instead of it waiting for an event that already happened.
  if (deferredInstallPrompt) onInstallAvailable?.();
  if (waitingWorker) onUpdateAvailable?.();

  if (registered) return;
  registered = true;

  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    installListeners.forEach((cb) => cb());
  });

  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
  });

  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.register("/sw.js").then((reg) => {
    // A worker can already be sitting in "waiting" if an update installed
    // in the background before this tab was even opened — that needs the
    // same treatment as one that installs while the page is open.
    if (reg.waiting && navigator.serviceWorker.controller) {
      waitingWorker = reg.waiting;
      updateListeners.forEach((cb) => cb());
    }
    reg.addEventListener("updatefound", () => {
      const newWorker = reg.installing;
      if (!newWorker) return;
      newWorker.addEventListener("statechange", () => {
        // "installed" + an existing controller means this is a genuine
        // update — a first-ever install on a brand new visit has nothing
        // to update FROM, so there's nothing worth announcing then.
        if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
          waitingWorker = newWorker;
          updateListeners.forEach((cb) => cb());
        }
      });
    });

    // The browser only checks for a new service worker on its own when
    // the page navigates, or roughly once every 24h if a tab is just left
    // open. Someone who installs this and keeps it open for hours —
    // exactly the coordinator-at-the-gate case this app is built for —
    // could otherwise sit on stale code the whole time without ever
    // seeing the update popup. reg.update() just re-checks whether a
    // newer sw.js exists (cheap — _headers already marks it no-cache); it
    // never itself applies anything, so this can't cause the disruptive
    // silent-swap this whole update flow was designed to avoid. Checks
    // every 30 minutes while open, plus once whenever the tab regains
    // focus (covers someone switching back after a while away).
    setInterval(() => reg.update().catch(() => {}), 30 * 60 * 1000);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") reg.update().catch(() => {});
    });
  }).catch(() => {});

  let reloadedAfterUpdate = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloadedAfterUpdate) return;
    reloadedAfterUpdate = true;
    window.location.reload();
  });
}

export function canInstall() {
  return !!deferredInstallPrompt;
}

export async function promptInstall() {
  if (!deferredInstallPrompt) return false;
  deferredInstallPrompt.prompt();
  const { outcome } = await deferredInstallPrompt.userChoice;
  deferredInstallPrompt = null;
  return outcome === "accepted";
}

export function applyUpdate() {
  if (!waitingWorker) return;
  waitingWorker.postMessage({ type: "SKIP_WAITING" });
}
